import React from "react";
import { alpha } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Divider,
  Switch,
  TextField,
  InputAdornment,
  Tooltip,
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BotConfig, PositionSizeMode } from "../../shared/types";

interface Props {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  isBotActive: boolean;
  loadingAction?: boolean;
  onPauseResume?: (enabled: boolean) => void;
  onForceClose?: () => void;
  onCloseAndPause?: () => void;
  editConfig?: BotConfig;
  token0Symbol?: string;
  token1Symbol?: string;
  walletToken0?: number;
  walletToken1?: number;
  handleFieldChange: (field: string, value: any) => void;
  handleNestedFieldChange: (parent: string, field: string, value: any) => void;
  handleDoubleNestedFieldChange: (
    parent: string,
    child: string,
    field: string,
    value: any
  ) => void;
  handleSave: () => void;
  savingConfig: boolean;
  loadingConfig?: boolean;
}

const SettingsDialog: React.FC<Props> = ({
  open,
  onClose,
  isMobile,
  isBotActive,
  loadingAction,
  onPauseResume,
  onForceClose,
  onCloseAndPause,
  editConfig,
  token0Symbol,
  token1Symbol,
  walletToken0,
  walletToken1,
  handleFieldChange,
  handleNestedFieldChange,
  handleDoubleNestedFieldChange,
  handleSave,
  savingConfig,
  loadingConfig,
}) => {
  const token0Label = token0Symbol || "Token 0";
  const token1Label = token1Symbol || "Token 1";
  const positionSizeMode: PositionSizeMode =
    editConfig?.positionSize?.mode || "PERCENT";

  const handlePositionSizeModeChange = (_: React.MouseEvent<HTMLElement>, mode: PositionSizeMode | null) => {
    if (!mode || !editConfig) return;

    handleFieldChange("positionSize", {
      mode,
      token0Amount: editConfig.positionSize?.token0Amount ?? 0,
      token1Amount: editConfig.positionSize?.token1Amount ?? 0,
    });
  };

  const handleTokenAmountChange = (
    field: "token0Amount" | "token1Amount",
    value: string
  ) => {
    const numericValue = Number(value);
    handleFieldChange("positionSize", {
      mode: "TOKEN_AMOUNTS",
      token0Amount: editConfig?.positionSize?.token0Amount ?? 0,
      token1Amount: editConfig?.positionSize?.token1Amount ?? 0,
      [field]: Number.isFinite(numericValue) ? numericValue : 0,
    });
  };

  const formatWalletAmount = (value?: number) =>
    typeof value === "number"
      ? value.toLocaleString(undefined, { maximumFractionDigits: 6 })
      : "-";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Phones get the shared bottom-sheet dialog style from the theme.
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { p: { xs: 0.5, sm: 1 } } }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          component="img"
          src="/logo.png"
          alt="Bot Logo"
          sx={{ height: 36, width: 36, objectFit: "contain" }}
        />
        <Typography component="span" variant="h6" fontWeight={700}>
          Bot configuration
        </Typography>
      </DialogTitle>
      <Divider />

      {/* Content */}
      <DialogContent dividers sx={{ p: 3 }}>
        {loadingConfig ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: 220 }}
          >
            <CircularProgress />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Loading Configuration...
            </Typography>
          </Stack>
        ) : editConfig ? (
          <Stack spacing={4}>
            {/* General Settings */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  General Settings
                </Typography>
                <Stack spacing={2}>
                  <Tooltip title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoOutlinedIcon fontSize="small" />
                      <Typography variant="body2">Choose whether new positions use a wallet percentage or fixed token amounts.</Typography>
                    </Box>
                  }>
                    <Stack spacing={1.5}>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        fullWidth
                        value={positionSizeMode}
                        onChange={handlePositionSizeModeChange}
                        disabled={loadingConfig}
                      >
                        <ToggleButton value="PERCENT">Percent</ToggleButton>
                        <ToggleButton value="TOKEN_AMOUNTS">Token amounts</ToggleButton>
                      </ToggleButtonGroup>

                      {positionSizeMode === "PERCENT" ? (
                        <TextField
                          label="Amount Percent"
                          type="number"
                          value={editConfig.amountPercent}
                          onChange={(e) =>
                            handleFieldChange("amountPercent", Number(e.target.value))
                          }
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                            inputProps: { min: 0, max: 100 },
                          }}
                          disabled={loadingConfig}
                        />
                      ) : (
                        <Stack spacing={1.5}>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <TextField
                              label={`${token0Label} amount`}
                              type="number"
                              value={editConfig.positionSize?.token0Amount ?? 0}
                              onChange={(e) =>
                                handleTokenAmountChange("token0Amount", e.target.value)
                              }
                              size="small"
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">{token0Label}</InputAdornment>
                                ),
                                inputProps: { min: 0, step: "any" },
                              }}
                              disabled={loadingConfig}
                            />
                            <TextField
                              label={`${token1Label} amount`}
                              type="number"
                              value={editConfig.positionSize?.token1Amount ?? 0}
                              onChange={(e) =>
                                handleTokenAmountChange("token1Amount", e.target.value)
                              }
                              size="small"
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">{token1Label}</InputAdornment>
                                ),
                                inputProps: { min: 0, step: "any" },
                              }}
                              disabled={loadingConfig}
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Wallet: {formatWalletAmount(walletToken0)} {token0Label} · {formatWalletAmount(walletToken1)} {token1Label}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Tooltip>

                  <Tooltip title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoOutlinedIcon fontSize="small" />
                      <Typography variant="body2">The maximum allowed percentage deviation between two tokens in a pool. If the deviation exceeds this value, it will trigger a rebalancing event. For example, if set to 20%, a rebalance will occur if one token is at 60% and the other at 40% (20% deviation).</Typography>
                    </Box>
                  }>
                    <TextField
                      label="Max Deviation Percent"
                      type="number"
                      value={editConfig.maxDeviationPercent}
                      onChange={(e) =>
                        handleFieldChange(
                          "maxDeviationPercent",
                          Number(e.target.value)
                        )
                      }
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                        inputProps: { min: 0, max: 100 },
                      }}
                      disabled={loadingConfig}
                    />
                  </Tooltip>

                  <Tooltip title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoOutlinedIcon fontSize="small" />
                      <Typography variant="body2">A value that, when the native token balance for gas fees is lower than this, will trigger a Telegram notification.</Typography>
                    </Box>
                  }>
                    <TextField
                      label="Native Token Min Amount For Fees"
                      type="number"
                      value={editConfig.nativeTokenMinAmountForFees}
                      onChange={(e) =>
                        handleFieldChange(
                          "nativeTokenMinAmountForFees",
                          Number(e.target.value)
                        )
                      }
                      size="small"
                      fullWidth
                      inputProps={{ step: 0.001, min: 0 }}
                      disabled={loadingConfig}
                    />
                  </Tooltip>
                </Stack>
              </Paper>

              {/* Range Settings */}
              {/* Range Settings */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Tooltip title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <InfoOutlinedIcon fontSize="small" />
                    <Typography variant="body2">For ATR mode, provide as many comma-separated values as there are bots in the family. For Percent mode, it's just a single percentage value.</Typography>
                  </Box>
                }>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Range Settings
                  </Typography>
                </Tooltip>
                <Stack spacing={2}>
                  <TextField
                    label="Range Mode"
                    select
                    value={editConfig.rangeConfig?.mode || "PERCENT"}
                    onChange={(e) =>
                      handleNestedFieldChange(
                        "rangeConfig",
                        "mode",
                        e.target.value
                      )
                    }
                    size="small"
                    fullWidth
                    SelectProps={{ native: true }}
                    disabled={loadingConfig}
                  >
                    <option value="PERCENT">Percent</option>
                    <option value="ATR">ATR</option>
                  </TextField>

                  <TextField
                    label={
                      editConfig.rangeConfig?.mode === "ATR"
                        ? "ATR Multipliers (comma-separated)"
                        : "Range Value"
                    }
                    type={
                      editConfig.rangeConfig?.mode === "PERCENT"
                        ? "number"
                        : "text"
                    }
                    value={
                      editConfig.rangeConfig?.mode === "PERCENT"
                        ? editConfig.rangeConfig?.values?.[0] || 0
                        : editConfig.rangeConfig?.values?.join(", ") || ""
                    }
                    onChange={(e) => {
                      let newValues: number[] = [];
                      if (editConfig.rangeConfig?.mode === "PERCENT") {
                        const numValue = Number(e.target.value);
                        if (!isNaN(numValue)) {
                          newValues = [numValue];
                        }
                      } else {
                        newValues = e.target.value
                          .split(",")
                          .map((s) => Number(s.trim()))
                          .filter((n) => !isNaN(n));
                      }
                      handleNestedFieldChange(
                        "rangeConfig",
                        "values",
                        newValues
                      );
                    }}
                    size="small"
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {editConfig.rangeConfig?.mode === "PERCENT"
                            ? "%"
                            : "ATR"}
                        </InputAdornment>
                      ),
                    }}
                    disabled={loadingConfig}
                  />
                </Stack>
              </Paper>

              {/* Exit Conditions */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Exit Conditions
                </Typography>
                <Stack spacing={2}>
                  {/* Range Exit */}
                  <Tooltip title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoOutlinedIcon fontSize="small" />
                      <Typography variant="body2">When the position is out of the configured range, it will be closed.</Typography>
                    </Box>
                  }>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">Range Exit Enabled</Typography>
                      <Switch
                        checked={
                          editConfig.exitConditions?.range?.enabled || false
                        }
                        onChange={(e) =>
                          handleNestedFieldChange("exitConditions", "range", {
                            enabled: e.target.checked,
                          })
                        }
                        disabled={loadingConfig}
                      />
                    </Stack>
                  </Tooltip>

                  {/* Slippage Exit */}
                  <Tooltip title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoOutlinedIcon fontSize="small" />
                      <Typography variant="body2">The bot will close a position if a simulated trade, using 'Percent Amount of TVL' as the trade size, results in slippage exceeding the 'Max Allowed Percent'. For example, if 'Max Allowed Percent' is 5% and 'Percent Amount of TVL' is 10%, the bot will close the position if a simulated 10% TVL trade incurs more than 0.5% slippage.</Typography>
                    </Box>
                  }>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">
                        Slippage Exit Enabled
                      </Typography>
                      <Switch
                        checked={
                          editConfig.exitConditions?.slippage?.enabled || false
                        }
                        onChange={(e) =>
                          handleDoubleNestedFieldChange(
                            "exitConditions",
                            "slippage",
                            "enabled",
                            e.target.checked
                          )
                        }
                        disabled={loadingConfig}
                      />
                    </Stack>
                  </Tooltip>

                  {/* Slippage config if enabled */}
                  {editConfig.exitConditions?.slippage?.enabled && (
                    <Stack spacing={2}>
                      <TextField
                        label="Max Allowed Percent"
                        type="number"
                        value={
                          editConfig.exitConditions?.slippage
                            ?.maxAllowedPercent || 0
                        }
                        onChange={(e) =>
                          handleDoubleNestedFieldChange(
                            "exitConditions",
                            "slippage",
                            "maxAllowedPercent",
                            Number(e.target.value)
                          )
                        }
                        size="small"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        disabled={loadingConfig}
                      />

                      <TextField
                        label="Percent Amount of TVL"
                        type="number"
                        value={
                          editConfig.exitConditions?.slippage
                            ?.percentAmountOfTVL || 0
                        }
                        onChange={(e) =>
                          handleDoubleNestedFieldChange(
                            "exitConditions",
                            "slippage",
                            "percentAmountOfTVL",
                            Number(e.target.value)
                          )
                        }
                        size="small"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        disabled={loadingConfig}
                      />
                    </Stack>
                  )}
                </Stack>
              </Paper>

              {/* Emergency Actions */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: (theme) => alpha(theme.palette.error.main, 0.3),
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.02),
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="error"
                  gutterBottom
                >
                  Emergency Actions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Controls to manually override bot behavior or intervene in
                  active positions.
                </Typography>

                <Stack spacing={2}>
                  {/* Force Exit Once (moved from Exit Conditions) */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Force Exit Once
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Forces a close of the current position without pausing the
                        bot.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={onForceClose}
                      disabled={loadingAction || loadingConfig}
                    >
                      Trigger Force Exit
                    </Button>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  {/* Pause / Resume */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {isBotActive ? "Pause Bot" : "Resume Bot"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isBotActive
                          ? "Pauses bot activity. Open positions remain active."
                          : "Resumes bot activity."}
                      </Typography>
                    </Box>
                    <Button
                      variant={isBotActive ? "outlined" : "contained"}
                      color={isBotActive ? "secondary" : "success"}
                      size="small"
                      onClick={() =>
                        onPauseResume && onPauseResume(!isBotActive)
                      }
                      disabled={loadingAction || loadingConfig}
                    >
                      {loadingAction ? (
                        <CircularProgress size={20} />
                      ) : isBotActive ? (
                        "Pause"
                      ) : (
                        "Resume"
                      )}
                    </Button>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  {/* Close & Pause */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500} color="error">
                        Close & Pause
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Immediately closes all open positions AND disables the
                        bot.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={onCloseAndPause}
                      disabled={loadingAction || loadingConfig}
                    >
                      {loadingAction ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Close & Pause"
                      )}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: 220 }}
          >
             <Typography variant="h6" color="error" gutterBottom>
               Configuration Not Found
             </Typography>
             <Typography variant="body2" color="text.secondary" align="center">
               Unable to load bot configuration. <br/>
               Please check backend logs for AWS credentials or connection errors.
             </Typography>
          </Stack>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{ p: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Stack
          direction="row"
          spacing={2}
          justifyContent="end"
          sx={{ width: "100%" }}
        >
          {/* Left side: Pause / Resume */}
        

          {/* Right side: Cancel / Save / Force Close */}
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={savingConfig || loadingConfig}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!editConfig || savingConfig || loadingConfig}
            >
              {savingConfig ? (
                <CircularProgress size={20} />
              ) : (
                "Save Configuration"
              )}
            </Button>
            
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
